import React from "react";
import { MoreVertical, ArrowUp, ArrowDown, Pencil, Archive, Circle } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { filesApi } from "@/lib/api";
import LogoWithHalo from "@/components/LogoWithHalo";

/**
 * EntityCard — HQ home card.
 *
 * Reads the derived `summary` prop (from /api/dashboard/entity_summaries)
 * for the eyebrow, priority line, and upcoming line. The card no longer
 * touches the deprecated manual `current_priority` / `next_action` /
 * `upcoming_event` fields, so anything shown here always reflects the live
 * canonical state of projects, tasks and calendar.
 */
export default function EntityCard({ entity, summary, onOpen, onEdit, onArchive, onMoveUp, onMoveDown }) {
  const bg = entity.background_image;
  const accent = entity.color || "#3B82F6";
  const mode = entity.display_mode || "detail";
  const isLogoMode = mode === "logo" && entity.logo_file_id;
  const isTextMode = mode === "text";
  const justify = isLogoMode || isTextMode ? "justify-center items-center" : "justify-end";

  const eyebrow = summary?.current_stage || entity.phase || entity.status || "Active";
  const priorityLine = summary?.priority_line;
  const nextLine = summary?.next_line;

  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-white/10 group cursor-pointer transition-transform transition-shadow duration-300 md:hover:-translate-y-1 md:hover:shadow-2xl flex flex-col ${justify} min-h-[132px] md:min-h-[288px] md:h-72 p-4 md:p-8`}
      style={{ borderColor: "rgba(255,255,255,0.08)" }}
      onClick={onOpen}
      data-testid={`entity-card-${entity.id}`}
      data-display-mode={mode}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = accent)}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)")}
    >
      {bg ? (
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bg})` }} />
      ) : (
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${accent}44 0%, #151A23 60%, #0B0E14 100%)` }} />
      )}
      <div className="absolute inset-0 entity-card-overlay" />

      {entity.is_archived && (
        <div className="absolute top-2.5 left-2.5 md:top-4 md:left-4 text-[9px] md:text-[10px] uppercase tracking-widest text-white/60 bg-white/10 backdrop-blur px-1.5 md:px-2 py-0.5 md:py-1 rounded z-10">
          Archived
        </div>
      )}

      <div className="absolute top-2 right-2 md:top-3 md:right-3 z-20" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={`entity-menu-${entity.id}`}
              className="w-8 h-8 rounded-md bg-black/50 backdrop-blur border border-white/10 flex items-center justify-center text-white/80 hover:text-white hover:border-white/25 transition-colors"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#151A23] border-white/10 text-white">
            <DropdownMenuItem onClick={onEdit} data-testid={`entity-edit-${entity.id}`} className="hover:bg-white/10 focus:bg-white/10">
              <Pencil className="w-4 h-4 mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onMoveUp} className="hover:bg-white/10 focus:bg-white/10">
              <ArrowUp className="w-4 h-4 mr-2" /> Move up
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onMoveDown} className="hover:bg-white/10 focus:bg-white/10">
              <ArrowDown className="w-4 h-4 mr-2" /> Move down
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem onClick={onArchive} data-testid={`entity-archive-${entity.id}`} className="text-red-400 hover:bg-red-500/10 focus:bg-red-500/10">
              <Archive className="w-4 h-4 mr-2" /> Archive
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isLogoMode && (
        <div className="relative z-10 w-full h-full flex items-center justify-center p-2 md:p-4" data-testid={`entity-card-logo-${entity.id}`} aria-label={entity.name}>
          <LogoWithHalo
            src={filesApi.downloadUrl(entity.logo_file_id)}
            alt={entity.name}
            variant="detail"
            wrapperClassName="max-w-full max-h-full w-full h-full flex items-center justify-center"
            imgClassName="max-w-full max-h-full w-auto h-auto object-contain"
            imgStyle={{ maxHeight: "calc(100% - 8px)" }}
            testid={`entity-card-logo-wrap-${entity.id}`}
          />
        </div>
      )}

      {isTextMode && (
        <div className="relative z-10 w-full flex items-center justify-center text-center px-3 md:px-6" data-testid={`entity-card-text-${entity.id}`}>
          <h3 className="font-display text-2xl md:text-5xl font-semibold tracking-tight text-white leading-tight break-words">
            {entity.name}
          </h3>
        </div>
      )}

      {!isLogoMode && !isTextMode && (
        <div className="relative z-10 space-y-2 md:space-y-3 pr-10">
          <div className="flex items-center gap-2 flex-wrap">
            <Circle className="w-2 h-2 fill-current flex-shrink-0" style={{ color: accent }} />
            <span className="text-[10px] md:text-xs uppercase tracking-widest md:tracking-[0.08em] font-medium" style={{ color: "rgba(255,255,255,0.55)" }}>
              {eyebrow}
            </span>
          </div>
          <h3 className="font-display text-xl md:text-3xl font-semibold tracking-tight text-white leading-tight break-words">
            {entity.name}
          </h3>
          <div className="space-y-1 md:space-y-1.5 md:pt-2" data-testid={`entity-card-summary-${entity.id}`}>
            {priorityLine ? (
              <div className="flex flex-col md:flex-row md:gap-2 md:items-start">
                <span className="text-[9px] md:text-xs uppercase tracking-wider text-white/40 md:min-w-[80px] md:pt-0.5">Priority</span>
                <span className="text-xs md:text-sm text-white/85 flex-1 break-words leading-snug">{priorityLine}</span>
              </div>
            ) : (
              summary && (
                <div className="text-xs md:text-sm text-white/40 italic">Nothing pressing.</div>
              )
            )}
            {nextLine && (
              <div className="flex flex-col md:flex-row md:gap-2 md:items-start">
                <span className="text-[9px] md:text-xs uppercase tracking-wider text-white/40 md:min-w-[80px] md:pt-0.5">Upcoming</span>
                <span className="text-xs md:text-sm text-white/85 flex-1 break-words leading-snug">{nextLine}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
